// Initialize Firebase and Google Maps integration
import { initializeApp } from "https://www.gstatic.com/firebasejs/11.6.0/firebase-app.js";
import { getAnalytics } from "https://www.gstatic.com/firebasejs/11.6.0/firebase-analytics.js";
import { getFirestore, collection, addDoc, serverTimestamp, onSnapshot } from "https://www.gstatic.com/firebasejs/11.6.0/firebase-firestore.js";

const firebaseConfig = {
  apiKey: "AIzaSyDnQmu4zipSe-047J_GQSrDg8X6LCQ1rJ8",
  authDomain: "heron-preston-a9d28.firebaseapp.com",
  projectId: "heron-preston-a9d28",
  storageBucket: "heron-preston-a9d28.firebasestorage.app",
  messagingSenderId: "235224766538",
  appId: "1:235224766538:web:e4457e51f79989ff452eb3",
  measurementId: "G-5HXNN36YNP"
};

const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const db = getFirestore(app);

// Global variables for the map, autocomplete, and current infoWindow
let map;
let autocomplete;
let currentInfoWindow = null;
// Global counter to track initialization attempts.
let mapInitAttempts = 0;
let mapInitialized = false;

// Global variable to track processed documents to avoid duplicates
let processedDocs = new Set();

// Set global flag for other scripts to check
window.mapInitialized = false;

// Define initMap function immediately to avoid race conditions
window.initMap = function() {
  // Check if DOM is ready and map element exists
  if (!document.getElementById('map')) {
    console.log("Map element not found, retrying...");
    setTimeout(window.initMap, 100);
    return;
  }

  // Check if Google Maps API is loaded
  if (typeof google === 'undefined' || !google.maps) {
    console.log("Google Maps API not loaded yet, retrying...");
    setTimeout(window.initMap, 100);
    return;
  }

  try {
    // Prevent multiple initializations
    if (mapInitialized || window.mapInitialized) {
      console.log("Map already initialized, skipping...");
      return;
    }

    // Increment the counter on each call.
    mapInitAttempts++;

    const initialLocation = { lat: 0, lng: 0 };
    map = new google.maps.Map(document.getElementById('map'), {
      zoom: 2,
      center: initialLocation
    });
  
    // Initialize Autocomplete for the manual address input.
    const manualInput = document.getElementById("manualLocation");
    if (manualInput) {
      autocomplete = new google.maps.places.Autocomplete(manualInput, {
        types: ["geocode"],
        fields: ["formatted_address", "geometry", "address_components"]
      });
    
      // Listen for a place selection.
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) {
          alert("No details available for the selected address.");
          return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        document.getElementById("latitude").value = lat;
        document.getElementById("longitude").value = lng;
        
        // Extract city from address components
        let city = "";
        if (place.address_components) {
          // Look for locality (city) or administrative_area_level_1 (state/province) as fallback
          const cityComponent = place.address_components.find(
            component => component.types.includes("locality")
          ) || place.address_components.find(
            component => component.types.includes("administrative_area_level_1")
          );
          if (cityComponent) {
            city = cityComponent.long_name;
            document.getElementById("city").value = city;
          }
        }
      
        // Optionally, re-center and zoom the map to the selected location.
        map.setCenter(place.geometry.location);
        map.setZoom(2);
      });
    } else {
      console.error("Manual address input element with id 'manualLocation' not found.");
    }
  
    // Begin listening for real-time updates.
    listenForGuestbookUpdates();
  
    // Mark as initialized and reset retry counter
    mapInitialized = true;
    window.mapInitialized = true;
    mapInitAttempts = 0;
    console.log("Map initialized successfully");
  } catch (error) {
    console.error("Error in initMap: ", error);
    // Retry only once.
    if (mapInitAttempts < 2) {
      console.log("Attempting to reinitialize the map...");
      // Use a short delay before retrying.
      setTimeout(window.initMap, 2000);
    } else {
      console.error("Map initialization failed after retry.");
    }
  }
};

// Also try to initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // If Google Maps API is already loaded, initialize immediately
  if (typeof google !== 'undefined' && google.maps) {
    window.initMap();
  }
  // Otherwise, initMap will be called by the Google Maps API callback
});

// Helper function to add a marker for a guestbook entry.
function addMarkerForEntry(data, docId, isPending) {
  // Skip if we've already processed this document and it's not pending
  if (!isPending && processedDocs.has(docId)) {
    console.log(`Skipping already processed document: ${docId}`);
    return;
  }
  
  if (data.location && ((data.location.latitude && data.location.longitude) || data.location.address)) {
    let markerPosition = {};
    if (data.location.latitude && data.location.longitude) {
      markerPosition = { lat: data.location.latitude, lng: data.location.longitude };
    } else {
      // If only an address is stored, you may want to geocode later.
      markerPosition = { lat: 0, lng: 0 };
    }
    // Create a colored circle icon for the marker
    const markerColor = data.markerColor || "#E56C3E";
    const iconOptions = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: markerColor,
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 1
    };
    
    const marker = new google.maps.Marker({
      position: markerPosition,
      map: map,
      title: data.name || "Guest",
      icon: iconOptions
    });
    
    // Enhanced timestamp handling
    let createdAtString = "N/A";
    console.log("Processing timestamp:", data.createdAt, "isPending:", isPending);
    
    if (data.createdAt) {
      // Handle both Firestore Timestamp objects and regular Date objects
      if (data.createdAt.toDate) {
        // Firestore Timestamp
        createdAtString = data.createdAt.toDate().toLocaleString();
      } else if (data.createdAt instanceof Date) {
        // Regular Date object
        createdAtString = data.createdAt.toLocaleString();
      } else if (data.createdAt.seconds) {
        // Firestore Timestamp with seconds property
        const date = new Date(data.createdAt.seconds * 1000);
        createdAtString = date.toLocaleString();
      }
    } else if (isPending) {
      // If pending and no timestamp, show "Just now"
      createdAtString = "Just now";
    } else {
      // If no timestamp, use current time as fallback for pending server timestamps
      console.log("No createdAt timestamp found, using current time as fallback");
      createdAtString = new Date().toLocaleString();
    }

    const infoWindowContent = `
  <div style="
    font-family: Arial, sans-serif;
    padding: 15px;
    max-width: 320px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  ">
    <div style="display: flex; align-items: flex-start;">
      <div style="
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background-color: ${markerColor};
        margin-right: 15px;
        border: 2px solid #f0f0f0;
        flex-shrink: 0;
      "></div>
      <div style="display: flex; flex-direction: column; flex: 1;">
        <h3 style="
          margin: 0 0 6px;
          font-size: 18px;
          color: #2c3e50;
          font-weight: 600;
        ">${data.name || "Guest"}</h3>
        <div style="
          font-size: 14px;
          color: #34495e;
          margin-bottom: 6px;
          line-height: 1.4;
        ">📍 ${data.location.city || "Unknown location"}</div>
        ${data.instagram ? `<div style="
          font-size: 13px;
          color: #3498db;
          margin-bottom: 6px;
          font-weight: 500;
        ">📷 <a href="https://instagram.com/${data.instagram.replace('@', '').trim()}" target="_blank" rel="noopener noreferrer" style="color: #3498db; text-decoration: none;">@${data.instagram.replace('@', '').trim()}</a></div>` : ''}
        <div style="
          font-size: 12px;
          color: #7f8c8d;
          font-style: italic;
        ">📅 ${createdAtString}</div>
      </div>
    </div>
  </div>
`;



// Create the info window
const infoWindow = new google.maps.InfoWindow({ content: infoWindowContent });

    
// Add click listener to marker
marker.addListener("click", function () {
  if (currentInfoWindow) {
    currentInfoWindow.close();
  }
  infoWindow.open(map, marker);
  currentInfoWindow = infoWindow;
});

// Add click listener to map to close info windows
google.maps.event.addListener(map, "click", function() {
  if (currentInfoWindow) {
    currentInfoWindow.close();
    currentInfoWindow = null;
  }
});

// Mark document as processed
processedDocs.add(docId);
  } else {
    console.error("Invalid location data for guest entry:", data);
  }
}

// Listen for real-time updates in the guestbook collection.
function listenForGuestbookUpdates() {
  onSnapshot(collection(db, "guestbook"), (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === "added") {
        // Get document data with metadata
        const docData = change.doc.data();
        const docId = change.doc.id;
        
        // Check if this is a pending write (local change before server confirmation)
        const isPending = change.doc.metadata.hasPendingWrites;
        
        // Add marker for each new document, but handle pending timestamps
        console.log(`Processing document ${docId}, pending: ${isPending}`, docData);
        addMarkerForEntry(docData, docId, isPending);
      }
      // Optional: You can also handle "modified" or "removed" changes as needed.
    });
  });
}

// Optional: Use My Location fallback function.
window.useMyLocation = function () {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        document.getElementById("latitude").value = lat;
        document.getElementById("longitude").value = lng;
        
        // Reverse geocode to get address and city
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            const place = results[0];
            // Set the formatted address
            document.getElementById("manualLocation").value = place.formatted_address;
            
            // Extract city from address components
            if (place.address_components) {
              const cityComponent = place.address_components.find(
                component => component.types.includes("locality")
              ) || place.address_components.find(
                component => component.types.includes("administrative_area_level_1")
              );
              if (cityComponent) {
                document.getElementById("city").value = cityComponent.long_name;
              }
            }
          } else {
            // If geocoding fails, just clear the address field
            document.getElementById("manualLocation").value = "";
          }
        });
        
        alert("Location captured via geolocation!");
      },
      (error) => {
        console.error("Error obtaining location", error);
        alert("Unable to get location. Please enter your address manually.");
      }
    );
  } else {
    alert("Geolocation is not supported by this browser.");
  }
};
document.getElementById("guestbookForm")?.addEventListener("submit", async (e) => {
  e.preventDefault();
  const formData = new FormData(e.target);

  // Handle location data
  const manualAddress = document.getElementById("manualLocation").value.trim();
  let lat = parseFloat(document.getElementById("latitude").value);
  let lng = parseFloat(document.getElementById("longitude").value);
  const city = document.getElementById("city").value.trim();
  
  // Get color value
  const markerColor = formData.get('markerColor') || "#E56C3E";

  try {
    await addDoc(collection(db, "guestbook"), {
      name: formData.get('guestName'),
      email: formData.get('email'), // Saved but not displayed
      instagram: formData.get('instagram'),
      markerColor: markerColor,
      location: {
        latitude: lat,
        longitude: lng,
        address: manualAddress,
        city: city
      },
      createdAt: serverTimestamp()
    });

    // Reset form
    e.target.reset();
    // Reset color picker button and input
    const colorPickerButton = document.getElementById('colorPickerButton');
    const markerColorInput = document.getElementById('markerColor');
    if (colorPickerButton) {
      colorPickerButton.style.backgroundColor = '#E56C3E';
    }
    if (markerColorInput) {
      markerColorInput.value = '#E56C3E';
    }
    alert("Entry saved successfully!");
  } catch (err) {
    console.error("Error saving entry:", err);
    alert("Error saving entry. Please try again.");
  }
});
